import { pad } from "../../../lib/format.js";
import { useTimeFormat } from "../../../lib/useTimeFormat.js";
import { useI18n } from "../../../lib/useI18n.js";
import { DurationStepper } from "./bits.js";
import { DateChip, EndsChip, StartsChip } from "./DateTimeChips.js";

interface Props {
  dateStr: string;
  hour: number | null;
  minute: number | null;
  allDay: boolean;
  duration: number;
  isEvent: boolean;
  deadlineStr: string;
  onDate: (dateStr: string, hour: number | null, minute: number | null) => void;
  onAllDay: (allDay: boolean) => void;
  onDuration: (minutes: number) => void;
  onDeadline: (dateStr: string) => void;
}

/** Time row — Starts · duration ⇄ all-day morph · Deadline (task) / Ends (event). */
export function TimeRow({
  dateStr, hour, minute, allDay, duration, isEvent, deadlineStr,
  onDate, onAllDay, onDuration, onDeadline,
}: Props) {
  const { t } = useI18n();
  const tf = useTimeFormat();
  const timed = !allDay && hour !== null && minute !== null;

  // Event end = start + duration; the Ends chip writes the duration back.
  const startDate = timed && dateStr ? new Date(`${dateStr}T${pad(hour!)}:${pad(minute!)}`) : null;
  const endDate = startDate ? new Date(startDate.getTime() + duration * 60_000) : null;

  const switchToTimed = () => {
    onAllDay(false);
    if (hour === null || minute === null) onDate(dateStr, 9, 0);
  };

  return (
    <div className="time-row">
      <div className="field starts-field">
        <div className="field-label">{t("taskForm.starts")}</div>
        <StartsChip
          dateStr={dateStr}
          hour={hour}
          minute={minute}
          allDay={allDay}
          hour12={tf.hour12}
          onDate={onDate}
        />
      </div>

      <div className="inline-field dur-field">
        <div className="dur-morph" style={{ width: allDay ? 150 : 196 }}>
          <div
            className="dur-edit-state"
            style={{
              opacity: allDay ? 0 : 1,
              transform: `scale(${allDay ? 0.92 : 1})`,
              pointerEvents: allDay ? "none" : "auto",
            }}
          >
            <DurationStepper minutes={duration} onChange={onDuration} />
            <button type="button" className="allday-btn" onClick={() => onAllDay(true)} aria-label={t("taskForm.switchToAllDay")}>
              <span>{t("taskForm.allDayLine1")}</span>
              <span>{t("taskForm.allDayLine2")}</span>
            </button>
          </div>
          <div
            className="dur-toggle-state"
            style={{
              opacity: allDay ? 1 : 0,
              transform: `scale(${allDay ? 1 : 0.92})`,
              pointerEvents: allDay ? "auto" : "none",
            }}
          >
            <div className="dur-toggle" role="tablist" aria-label={t("taskForm.durationOrAllDay")}>
              <span className="dur-thumb" data-pos={allDay ? "right" : "left"} />
              <button type="button" role="tab" className={!allDay ? "is-on" : ""} onClick={switchToTimed}>
                {t("taskForm.duration")}
              </button>
              <button type="button" role="tab" className={allDay ? "is-on" : ""} onClick={() => onAllDay(true)}>
                {t("taskForm.allDay")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isEvent ? (
        <div className="field">
          <div className="field-label">{t("taskForm.ends")}</div>
          <EndsChip
            end={endDate}
            startMs={startDate ? startDate.getTime() : null}
            hour12={tf.hour12}
            allDay={allDay}
            hasStart={!!dateStr}
            onDuration={onDuration}
          />
        </div>
      ) : (
        <div className="field">
          <div className="field-label">{t("taskForm.deadline")}</div>
          <DateChip value={deadlineStr} onChange={onDeadline} clearable ariaLabel={t("taskForm.deadline")} align="right" />
        </div>
      )}
    </div>
  );
}
